import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { AlertCircle, CheckCircle, Eye, RotateCcw, Plus, X, ChevronDown, ChevronUp, Trash2, Globe, Search, Link2, Loader2, Zap, Wrench, Sparkles } from 'lucide-react';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Loader2 },
  fixed: { label: 'Fixed', color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: CheckCircle },
  verified: { label: 'Verified', color: 'bg-green-100 text-green-800 border-green-200', icon: Eye },
  reopened: { label: 'Reopened', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: RotateCcw },
};

const TYPE_CONFIG = {
  bug_fix: { label: 'Bug Fix', color: 'bg-red-50 text-red-700 border-red-200', icon: Wrench },
  enhancement: { label: 'Enhancement', color: 'bg-purple-50 text-purple-700 border-purple-200', icon: Zap },
  new_feature: { label: 'New Feature', color: 'bg-teal-50 text-teal-700 border-teal-200', icon: Sparkles },
};

const AdminIssueLog = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', relatedTo: '', issueType: '' });
  const [updateForm, setUpdateForm] = useState({ status: '', note: '', verifiedOnProduction: true, linkTo: '', credits: '', issueType: '' });
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [message, setMessage] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchIssues(); }, [filterStatus]);

  const fetchIssues = async (search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (search) params.set('search', search);
      const q = params.toString() ? `?${params.toString()}` : '';
      const res = await axiosInstance.get(`/admin/issues${q}`);
      setIssues(res.data.issues || []);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load issues' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchIssues(searchTerm);
  const flash = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage(null), 4000); };

  const createIssue = async () => {
    if (!form.title.trim()) return flash('error', 'Title is required');
    if (!form.issueType) return flash('error', 'Issue type is required');
    try {
      const res = await axiosInstance.post('/admin/issues', form);
      flash('success', res.data.message);
      setShowAdd(false);
      setForm({ title: '', description: '', relatedTo: '', issueType: '' });
      fetchIssues(searchTerm);
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to create');
    }
  };

  const updateIssue = async (issueId) => {
    if (!updateForm.status && !updateForm.note && !updateForm.linkTo && !updateForm.credits) return flash('error', 'Make a change before saving');
    try {
      const payload = { ...updateForm, credits: parseFloat(updateForm.credits) || 0 };
      await axiosInstance.put(`/admin/issues/${issueId}`, payload);
      flash('success', `Issue ${issueId} updated`);
      setUpdatingId(null);
      setUpdateForm({ status: '', note: '', verifiedOnProduction: true, linkTo: '', credits: '', issueType: '' });
      fetchIssues(searchTerm);
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteIssue = async (issueId) => {
    if (!window.confirm(`Delete issue ${issueId}?`)) return;
    try {
      await axiosInstance.delete(`/admin/issues/${issueId}`);
      flash('success', 'Issue deleted');
      fetchIssues(searchTerm);
    } catch (err) {
      flash('error', 'Failed to delete');
    }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const getIssueTitle = (id) => {
    const iss = issues.find(i => i.id === id);
    return iss ? iss.title : id;
  };

  // Compute credits breakdown for reopened issues
  const getCreditsBreakdown = (issue) => {
    const history = issue.history || [];
    const sessions = [];
    let current = { label: 'Initial', credits: 0, entries: [] };

    for (const h of history) {
      if (h.status === 'reopened' && current.entries.length > 0) {
        sessions.push(current);
        current = { label: `Reopen #${sessions.length}`, credits: 0, entries: [] };
      }
      current.credits += (h.credits || 0);
      if (h.credits > 0) current.entries.push(h);
    }
    sessions.push(current);
    return sessions.filter(s => s.credits > 0);
  };

  const totalCreditsAll = issues.reduce((sum, i) => sum + (i.totalCredits || 0), 0);
  const recurringIssues = issues.filter(i => i.reopenCount > 0 || (i.linkedIssues && i.linkedIssues.length > 0));

  return (
    <div className="space-y-6" data-testid="issue-log-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const count = issues.filter(i => i.status === key).length;
          const Icon = cfg.icon;
          return (
            <button key={key} onClick={() => setFilterStatus(filterStatus === key ? '' : key)}
              className={`bg-white rounded-xl shadow-sm border p-4 text-left transition-all ${filterStatus === key ? 'ring-2 ring-purple-400' : ''}`}
              data-testid={`issue-count-${key}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className="h-4 w-4 text-gray-500" />
                <span className="text-xs text-gray-500 font-medium uppercase">{cfg.label}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{count}</p>
            </button>
          );
        })}
      </div>

      {/* Credits Summary */}
      {totalCreditsAll > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-4 flex items-center justify-between" data-testid="total-credits-summary">
          <div>
            <p className="text-xs text-gray-500 font-medium uppercase">Total Credits Used (All Issues)</p>
            <p className="text-2xl font-bold text-gray-900">{totalCreditsAll.toFixed(1)}</p>
          </div>
          <div className="flex gap-4 text-xs text-gray-500">
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
              const typeCredits = issues.filter(i => i.issueType === key).reduce((s, i) => s + (i.totalCredits || 0), 0);
              if (typeCredits === 0) return null;
              return (
                <div key={key} className="text-center">
                  <p className="font-bold text-gray-900">{typeCredits.toFixed(1)}</p>
                  <p>{cfg.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recurring Issues Banner */}
      {recurringIssues.length > 0 && !filterStatus && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="recurring-issues-banner">
          <p className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
            <RotateCcw className="h-4 w-4" />
            Recurring / Linked Issues ({recurringIssues.length})
          </p>
          <div className="space-y-1">
            {recurringIssues.map(i => (
              <div key={i.id} className="flex items-center gap-2 text-xs flex-wrap">
                <code className="text-amber-700 font-bold">{i.id}</code>
                <span className="text-gray-700">{i.title}</span>
                {i.reopenCount > 0 && <span className="text-red-600 font-bold">Reopened {i.reopenCount}x</span>}
                {i.linkedIssues?.length > 0 && <span className="text-blue-600">{i.linkedIssues.length} linked</span>}
                {(i.totalCredits || 0) > 0 && <span className="text-gray-600">{i.totalCredits.toFixed(1)} credits</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {message && (
        <div className={`p-3 rounded-lg text-sm font-medium ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {/* Search + Actions */}
      <div className="flex flex-wrap gap-2">
        <div className="flex-1 min-w-[200px] flex gap-1">
          <input
            placeholder="Search issues by title, description, or ID..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
            data-testid="issue-search-input"
          />
          <button onClick={handleSearch} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200" data-testid="issue-search-btn">
            <Search className="h-4 w-4 text-gray-600" />
          </button>
        </div>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700" data-testid="add-issue-btn">
          <Plus className="h-4 w-4" /> Log Issue
        </button>
        {(filterStatus || searchTerm) && (
          <button onClick={() => { setFilterStatus(''); setSearchTerm(''); fetchIssues(); }} className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200">
            Clear filters
          </button>
        )}
      </div>

      {/* Add Issue Form */}
      {showAdd && (
        <div className="bg-white rounded-xl shadow-sm border p-5" data-testid="add-issue-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Log New Issue</h3>
            <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
          </div>
          <div className="space-y-3">
            <input placeholder="Issue title *" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="issue-title-input" />
            <textarea placeholder="Description / details" value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg text-sm" rows={3} data-testid="issue-desc-input" />
            <div>
              <label className="text-xs text-gray-500 font-medium">Issue Type *</label>
              <div className="flex gap-2 mt-1.5">
                {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
                  const TypeIcon = cfg.icon;
                  return (
                    <button key={key} onClick={() => setForm({...form, issueType: key})}
                      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-all ${form.issueType === key ? cfg.color + ' ring-2 ring-offset-1 ring-purple-300' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                      data-testid={`type-btn-${key}`}>
                      <TypeIcon className="h-3.5 w-3.5" />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">Related to existing issue (optional)</label>
              <select value={form.relatedTo} onChange={e => setForm({...form, relatedTo: e.target.value})}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="issue-related-select">
                <option value="">None — this is a new issue</option>
                {issues.map(i => <option key={i.id} value={i.id}>{i.id}: {i.title}</option>)}
              </select>
            </div>
          </div>
          <button onClick={createIssue} className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700" data-testid="save-issue-btn">
            Create Issue
          </button>
        </div>
      )}

      {/* Issues List */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">Loading...</div>
        ) : issues.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">
            <AlertCircle className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p>{filterStatus ? `No ${STATUS_CONFIG[filterStatus]?.label || filterStatus} issues` : searchTerm ? 'No issues match your search' : 'No issues logged yet'}</p>
          </div>
        ) : (
          issues.map((issue) => {
            const cfg = STATUS_CONFIG[issue.status] || STATUS_CONFIG.open;
            const Icon = cfg.icon;
            const typeCfg = TYPE_CONFIG[issue.issueType];
            const TypeIcon = typeCfg?.icon;
            const isExpanded = expandedId === issue.id;
            const isUpdating = updatingId === issue.id;
            const hasLinks = (issue.relatedTo || (issue.linkedIssues && issue.linkedIssues.length > 0));
            const creditsBreakdown = getCreditsBreakdown(issue);

            return (
              <div key={issue.id} className={`bg-white rounded-xl shadow-sm border overflow-hidden ${issue.reopenCount > 0 ? 'border-l-4 border-l-amber-400' : ''}`} data-testid={`issue-card-${issue.id}`}>
                {/* Header */}
                <div className="p-4 flex items-start gap-3 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : issue.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <code className="text-xs font-bold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded" data-testid={`issue-id-${issue.id}`}>{issue.id}</code>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${cfg.color}`} data-testid={`issue-status-${issue.id}`}>
                        <Icon className="h-3 w-3" /> {cfg.label}
                      </span>
                      {typeCfg && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${typeCfg.color}`} data-testid={`issue-type-${issue.id}`}>
                          <TypeIcon className="h-3 w-3" /> {typeCfg.label}
                        </span>
                      )}
                      {issue.reopenCount > 0 && (
                        <span className="text-xs text-red-600 font-bold bg-red-50 px-1.5 py-0.5 rounded" data-testid={`issue-reopen-count-${issue.id}`}>
                          Reopened {issue.reopenCount}x
                        </span>
                      )}
                      {issue.verifiedOnProduction && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded">
                          <Globe className="h-3 w-3" /> Production
                        </span>
                      )}
                      {hasLinks && (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium bg-blue-50 px-1.5 py-0.5 rounded">
                          <Link2 className="h-3 w-3" /> Linked
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">{issue.title}</h3>
                    {issue.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{issue.description}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-xs text-gray-400">{formatDate(issue.createdAt)}</span>
                    {(issue.totalCredits || 0) > 0 && (
                      <span className="text-xs font-bold text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded" data-testid={`issue-credits-${issue.id}`}>
                        {issue.totalCredits.toFixed(1)} credits
                      </span>
                    )}
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </div>

                {/* Expanded */}
                {isExpanded && (
                  <div className="border-t px-4 py-3 bg-gray-50 space-y-4">
                    {/* Credits Breakdown (for reopened issues) */}
                    {creditsBreakdown.length > 0 && (
                      <div data-testid={`issue-credits-breakdown-${issue.id}`}>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Credits Breakdown</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {creditsBreakdown.map((session, idx) => (
                            <div key={idx} className={`p-2.5 rounded-lg border text-center ${idx > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-100 border-gray-200'}`}>
                              <p className="text-[10px] text-gray-500 font-medium uppercase">{session.label}</p>
                              <p className="text-lg font-bold text-gray-900">{session.credits.toFixed(1)}</p>
                              <p className="text-[10px] text-gray-400">credits</p>
                            </div>
                          ))}
                          <div className="p-2.5 rounded-lg border bg-purple-50 border-purple-200 text-center">
                            <p className="text-[10px] text-purple-600 font-medium uppercase">Total</p>
                            <p className="text-lg font-bold text-purple-700">{(issue.totalCredits || 0).toFixed(1)}</p>
                            <p className="text-[10px] text-purple-400">credits</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Linked Issues */}
                    {hasLinks && (
                      <div data-testid={`issue-links-${issue.id}`}>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Linked Issues</p>
                        {issue.relatedTo && (
                          <div className="flex items-center gap-2 text-xs mb-1">
                            <span className="text-gray-500">Parent:</span>
                            <button onClick={(e) => { e.stopPropagation(); setExpandedId(issue.relatedTo); }}
                              className="text-purple-600 font-bold hover:underline">{issue.relatedTo}</button>
                            <span className="text-gray-500">{getIssueTitle(issue.relatedTo)}</span>
                          </div>
                        )}
                        {issue.linkedIssues?.map(lid => (
                          <div key={lid} className="flex items-center gap-2 text-xs mb-1">
                            <span className="text-gray-500">Related:</span>
                            <button onClick={(e) => { e.stopPropagation(); setExpandedId(lid); }}
                              className="text-purple-600 font-bold hover:underline">{lid}</button>
                            <span className="text-gray-500">{getIssueTitle(lid)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* History */}
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">History</p>
                      <div className="space-y-2">
                        {(issue.history || []).map((h, i) => {
                          const hCfg = h.status ? STATUS_CONFIG[h.status] : null;
                          return (
                            <div key={i} className="flex items-start gap-2 text-xs" data-testid={`issue-history-${issue.id}-${i}`}>
                              <span className="text-gray-400 min-w-[70px] flex-shrink-0">{formatDate(h.date)}</span>
                              {hCfg && <span className={`px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${hCfg.color}`}>{hCfg.label}</span>}
                              <span className="text-gray-700 flex-1">{h.note}</span>
                              {h.credits > 0 && <span className="text-gray-900 font-bold bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">{h.credits} cr</span>}
                              <span className="text-gray-400 flex-shrink-0">— {h.by}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Update form */}
                    {!isUpdating ? (
                      <div className="flex gap-2 pt-1">
                        <button onClick={(e) => { e.stopPropagation(); setUpdatingId(issue.id); setUpdateForm({ status: '', note: '', verifiedOnProduction: true, linkTo: '', credits: '', issueType: '' }); }}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700" data-testid={`update-issue-btn-${issue.id}`}>
                          Update Status
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); deleteIssue(issue.id); }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`delete-issue-btn-${issue.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3 bg-white p-3 rounded-lg border" data-testid={`update-form-${issue.id}`}>
                        {/* Status buttons */}
                        <div className="flex gap-2 flex-wrap">
                          {Object.entries(STATUS_CONFIG).map(([key, c]) => (
                            <button key={key} onClick={() => setUpdateForm({...updateForm, status: key})}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${updateForm.status === key ? c.color + ' ring-2 ring-offset-1 ring-purple-300' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                              data-testid={`status-btn-${key}`}>
                              {c.label}
                            </button>
                          ))}
                        </div>

                        {/* Credits input */}
                        <div className="flex gap-3 items-end">
                          <div className="flex-1">
                            <label className="text-xs text-gray-500 font-medium">Credits used for this update</label>
                            <input type="number" min="0" step="0.5" placeholder="0" value={updateForm.credits}
                              onChange={e => setUpdateForm({...updateForm, credits: e.target.value})}
                              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" data-testid={`update-credits-${issue.id}`} />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-gray-500 font-medium">Change type (optional)</label>
                            <select value={updateForm.issueType} onChange={e => setUpdateForm({...updateForm, issueType: e.target.value})}
                              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm">
                              <option value="">Keep current</option>
                              {Object.entries(TYPE_CONFIG).map(([key, cfg]) => <option key={key} value={key}>{cfg.label}</option>)}
                            </select>
                          </div>
                        </div>

                        {/* Note */}
                        <textarea placeholder="Note (what was done, why reopened, etc.)" value={updateForm.note}
                          onChange={e => setUpdateForm({...updateForm, note: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} data-testid={`update-note-${issue.id}`} />

                        {updateForm.status === 'verified' && (
                          <label className="flex items-center gap-2 text-xs text-gray-700">
                            <input type="checkbox" checked={updateForm.verifiedOnProduction}
                              onChange={e => setUpdateForm({...updateForm, verifiedOnProduction: e.target.checked})}
                              className="rounded" data-testid="verified-prod-checkbox" />
                            Verified on live production site
                          </label>
                        )}

                        {/* Link to */}
                        <div>
                          <label className="text-xs text-gray-500">Link to another issue (optional)</label>
                          <select value={updateForm.linkTo} onChange={e => setUpdateForm({...updateForm, linkTo: e.target.value})}
                            className="w-full mt-1 px-3 py-1.5 border rounded-lg text-xs">
                            <option value="">No link</option>
                            {issues.filter(i => i.id !== issue.id).map(i => <option key={i.id} value={i.id}>{i.id}: {i.title}</option>)}
                          </select>
                        </div>

                        <div className="flex gap-2">
                          <button onClick={() => updateIssue(issue.id)}
                            className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700" data-testid={`confirm-update-${issue.id}`}>
                            Save Update
                          </button>
                          <button onClick={() => setUpdatingId(null)}
                            className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdminIssueLog;
